import { Home, Search, BarChart3, Calculator, Map } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import { cn } from "@/lib/utils";
import { prefetchHandlers } from "@/lib/routePrefetch";

const items = [
  { label: "Home", url: "/", icon: Home },
  { label: "Search", url: "/search", icon: Search },
  { label: "Market", url: "/market", icon: BarChart3 },
  { label: "Tools", url: "/calculators", icon: Calculator },
  { label: "Journey", url: "/journey", icon: Map },
];

export function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const activeIndex = items.findIndex(({ url }) =>
    url === "/" ? pathname === "/" : pathname.startsWith(url)
  );

  const focusTab = (index: number) => {
    const len = items.length;
    const next = ((index % len) + len) % len;
    tabRefs.current[next]?.focus();
    navigate(items[next].url);
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        focusTab(idx + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        focusTab(idx - 1);
        break;
      case "Home":
        e.preventDefault();
        focusTab(0);
        break;
      case "End":
        e.preventDefault();
        focusTab(items.length - 1);
        break;
    }
  };

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden"
    >
      <div role="tablist" aria-orientation="horizontal" className="flex items-center justify-around h-14">
        {items.map(({ label, url, icon: Icon }, idx) => {
          const active = idx === activeIndex;
          return (
            <NavLink
              key={url}
              to={url}
              role="tab"
              aria-selected={active}
              aria-label={label}
              tabIndex={active ? 0 : -1}
              ref={(el) => (tabRefs.current[idx] = el)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-muted-foreground transition-colors active:scale-90 active:opacity-70 transition-transform duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                active && "text-primary"
              )}
              {...prefetchHandlers(url)}
            >
              <Icon aria-hidden="true" className={cn("h-5 w-5 transition-transform duration-150", active && "text-primary animate-scale-in")} />
              <span className={cn("text-[10px]", active && "font-semibold")}>{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
